import type { Database } from '@shkola/db-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { classLabel } from './contexts';
import { must, useDb } from './db';
import { type AbsenceStatement, OFFLINE_ACTIONS } from './offline';

export type AbsenceReason = Database['public']['Enums']['absence_reason'];
export type RequestStatus = Database['public']['Enums']['request_status'];
export const ABSENCE_REASONS: readonly AbsenceReason[] = [
  'illness',
  'medical',
  'family',
  'alarm_night',
  'other',
];

export interface AbsenceRequest {
  id: string;
  studentId: string;
  studentName: string;
  classLabel: string | null;
  /** null when the reader may not see the author (e.g. the other parent) */
  authorName: string | null;
  fromDate: string;
  toDate: string;
  reason: AbsenceReason;
  comment: string | null;
  status: RequestStatus;
  reviewComment: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

const REQUEST_SELECT = `
  id, student_member_id, from_date, to_date, reason, comment, status, review_comment,
  reviewed_at, created_at,
  student:school_members!absence_requests_school_id_student_member_id_fkey(
    first_name, last_name,
    enrollments:class_enrollments!class_enrollments_school_id_student_member_id_fkey(
      class:classes!class_enrollments_school_id_class_id_fkey(grade, letter)
    )
  ),
  author:school_members!absence_requests_school_id_author_member_id_fkey(first_name, last_name)
`;

/**
 * Statements about absence: of one child (the family), or every statement the person may
 * decide on (the homeroom teacher: their class; the administration: the school). Newest first.
 */
export function useAbsenceRequests(filter: { studentId: string } | { schoolId: string }) {
  const db = useDb();
  return useQuery({
    queryKey: ['absence-requests', filter],
    queryFn: async (): Promise<AbsenceRequest[]> => {
      let query = db.from('absence_requests').select(REQUEST_SELECT);
      query =
        'studentId' in filter
          ? query.eq('student_member_id', filter.studentId)
          : query.eq('school_id', filter.schoolId);
      const rows = must(await query.order('created_at', { ascending: false }).limit(300));
      return rows.map((r) => {
        const cls = r.student.enrollments[0]?.class;
        return {
          id: r.id,
          studentId: r.student_member_id,
          studentName: `${r.student.last_name} ${r.student.first_name}`,
          classLabel: cls ? classLabel(cls) : null,
          // the embedded author comes back empty when RLS hides that person, whatever the types say
          authorName: (r.author as typeof r.author | null)
            ? `${r.author.last_name} ${r.author.first_name}`
            : null,
          fromDate: r.from_date,
          toDate: r.to_date,
          reason: r.reason,
          comment: r.comment,
          status: r.status,
          reviewComment: r.review_comment,
          reviewedAt: r.reviewed_at,
          createdAt: r.created_at,
        };
      });
    },
  });
}

/** Which absences of a course are covered by a statement (no reasons): for the journal. */
export function useCourseAbsenceNotes(courseId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['absence-requests', 'course', courseId],
    enabled: courseId !== undefined,
    queryFn: async () =>
      must(await db.rpc('course_absence_notes', { p_course: courseId as string })),
  });
}

/** True when a statement (not rejected) covers that day for that student. */
export function isCovered(
  notes: ReadonlyArray<{ student_member_id: string; from_date: string; to_date: string }>,
  studentId: string,
  date: string,
) {
  return notes.some(
    (n) => n.student_member_id === studentId && n.from_date <= date && date <= n.to_date,
  );
}

export function useRequestActions() {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => client.invalidateQueries({ queryKey: ['absence-requests'] });
  return {
    /** Works without connection; `clientId` makes a resend after a lost connection harmless. */
    submit: useMutation<unknown, Error, AbsenceStatement>({
      mutationKey: OFFLINE_ACTIONS.submitRequest,
    }),
    review: useMutation({
      mutationFn: async (a: { id: string; status: RequestStatus; comment: string }) =>
        must(
          await db
            .from('absence_requests')
            .update({ status: a.status, review_comment: a.comment.trim() || null })
            .eq('id', a.id),
        ),
      onSettled: invalidate,
    }),
    withdraw: useMutation({
      mutationFn: async (id: string) =>
        must(await db.from('absence_requests').delete().eq('id', id)),
      onSettled: invalidate,
    }),
  };
}
